import { api } from './client'
import type { PillTone } from '../components/ui'

/** Client di `DomainsController` (`/api/domains`): i domini dei clienti, scadenze, iWebLab, hosting e DNS. */
export type DomainHosting = 'vps' | 'iweblab' | 'esterno' | 'nessuno'

export const DOMAIN_HOSTINGS: { value: DomainHosting; label: string }[] = [
  { value: 'vps', label: 'VPS Equal' },
  { value: 'iweblab', label: 'iWebLab' },
  { value: 'esterno', label: 'Esterno' },
  { value: 'nessuno', label: 'Nessun sito' },
]

export interface DomainRow {
  id: number
  customerId: number | null
  customerName: string | null
  name: string
  registrar: string | null
  registeredOn: string | null
  expiresOn: string | null
  providerSince: string | null
  providerDueOn: string | null
  autoRenew: boolean | null
  providerStatus: string | null
  hosting: DomainHosting
  hostingServer: string | null
  dnsProvider: string | null
  hostedByUs: boolean
  source: 'manuale' | 'piattaforma'
  lastCheckedAt: string | null
}

export interface DomainList {
  total: number
  page: number
  pageSize: number
  /** Il giorno di oggi per il server (Italia): le evidenze delle scadenze si calcolano da qui. */
  today: string
  expiredCount: number
  expiringSoonCount: number
  hostedCount: number
  items: DomainRow[]
}

export interface DomainDetail extends Omit<DomainRow, 'customerName'> {
  customer: { id: number; name: string } | null
  notes: string | null
  createdAt: string
  updatedAt: string | null
}

export interface DomainRequest {
  customerId: number | null
  name: string
  registrar: string | null
  registeredOn: string | null
  expiresOn: string | null
  providerSince: string | null
  providerDueOn: string | null
  autoRenew: boolean | null
  providerStatus: string | null
  hosting: DomainHosting
  hostingServer: string | null
  dnsProvider: string | null
  notes: string | null
}

export interface DomainListParams {
  customerId?: number
  q?: string
  hosting?: DomainHosting | ''
  hostedByUs?: boolean | null
  expiringWithinDays?: number | null
  expired?: boolean
  page?: number
  pageSize?: number
}

export function domainListQuery(p: DomainListParams = {}): string {
  const s = new URLSearchParams()
  if (p.customerId) s.set('customerId', String(p.customerId))
  if (p.q) s.set('q', p.q)
  if (p.hosting) s.set('hosting', p.hosting)
  if (p.hostedByUs === true || p.hostedByUs === false) s.set('hostedByUs', String(p.hostedByUs))
  if (p.expiringWithinDays !== null && p.expiringWithinDays !== undefined) s.set('expiringWithinDays', String(p.expiringWithinDays))
  if (p.expired) s.set('expired', 'true')
  s.set('page', String(p.page ?? 1))
  s.set('pageSize', String(p.pageSize ?? 50))
  return s.toString()
}

export const domainsApi = {
  list: (p: DomainListParams = {}) => api<DomainList>(`/api/domains?${domainListQuery(p)}`),
  get: (id: number) => api<DomainDetail>(`/api/domains/${id}`),
  create: (data: DomainRequest) => api<{ id: number }>('/api/domains', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: DomainRequest) => api<void>(`/api/domains/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id: number) => api<void>(`/api/domains/${id}`, { method: 'DELETE' }),
}

/** Giorni da `today` a `iso` (entrambi YYYY-MM-DD), negativi se già passato. */
export function daysUntil(iso: string, today: string): number {
  const a = Date.UTC(...ymd(today))
  const b = Date.UTC(...ymd(iso))
  return Math.round((b - a) / 86_400_000)
}

function ymd(iso: string): [number, number, number] {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number) as [number, number, number]
  return [y, m - 1, d]
}

export type ExpiryLevel = 'scaduto' | 'entro30' | 'entro90' | 'ok' | 'nessuna'

/** Scaduto in rosso, entro 30 giorni in arancio, entro 90 in giallo. */
export function expiryLevel(expiresOn: string | null, today: string): ExpiryLevel {
  if (!expiresOn) return 'nessuna'
  const d = daysUntil(expiresOn, today)
  if (d < 0) return 'scaduto'
  if (d <= 30) return 'entro30'
  if (d <= 90) return 'entro90'
  return 'ok'
}

/** Il testo breve accanto alla data: «scaduto da 3 giorni», «tra 12 giorni», «oggi». */
export function expiryHint(expiresOn: string | null, today: string): string {
  if (!expiresOn) return ''
  const d = daysUntil(expiresOn, today)
  if (d === 0) return 'scade oggi'
  if (d < 0) return d === -1 ? 'scaduto da 1 giorno' : `scaduto da ${-d} giorni`
  return d === 1 ? 'tra 1 giorno' : `tra ${d} giorni`
}

/** Classe e tono della pillola della scadenza; arancio e giallo sono classi di `domains.css` sopra `md-pill`. */
export function expiryPill(level: ExpiryLevel): { tone: PillTone; className?: string } {
  switch (level) {
    case 'scaduto': return { tone: 'error' }
    case 'entro30': return { tone: 'warning', className: 'domain-due--orange' }
    case 'entro90': return { tone: 'warning', className: 'domain-due--yellow' }
    default: return { tone: 'neutral' }
  }
}

/** Dove sta il sito: «VPS», «iWebLab smm1», «Esterno (Aruba)», «Nessun sito». */
export function hostingLabel(d: Pick<DomainRow, 'hosting' | 'hostingServer'>): string {
  const server = d.hostingServer?.trim()
  switch (d.hosting) {
    case 'vps': return server && server.toLowerCase() !== 'vps' ? `VPS ${server}` : 'VPS'
    case 'iweblab': return server ? `iWebLab ${server}` : 'iWebLab'
    case 'esterno': return server ? `Esterno (${server})` : 'Esterno'
    default: return 'Nessun sito'
  }
}

/** Il nome come lo salva il server: minuscolo, senza http(s):// né percorso, senza punto finale. */
export function normalizeDomainName(raw: string): string {
  return raw.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/[/?#].*$/, '').replace(/\.+$/, '')
}

export const yesNo = (v: boolean | null) => (v === null ? '—' : v ? 'Sì' : 'No')
